import React, { useEffect, useRef, useState } from "react";
import Highcharts from "highcharts";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  Card,
  ChartContainer,
  LegendChip,
  LegendDot,
  LegendRow,
  SummaryItem,
  SummaryLabel,
  SummaryRow,
  SummaryValue
} from "./styled";

import { useUsersSlice } from "../slice/index";
import {
  selectMarketplaceAnalytics,
  selectMarketplaceChart
} from "../slice/selectors";
import LoadingSpinner from "components/PageLoader/Spinner";

import { ACTION_COLORS, FALLBACK_COLORS } from "../data";

const MERGED_INTO_CONTACT = ["call", "whatsapp"];
const MARKETPLACE_STATS_ORDER = ["contact", "share", "view"];

const MarketplaceStats = () => {
  const { userId } = useParams();
  const dispatch = useDispatch();
  const { actions } = useUsersSlice();
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const { data: analyticsData, loading: analyticsLoading } = useSelector(
    selectMarketplaceAnalytics
  );
  const { data: chartData, loading: chartLoading } = useSelector(
    selectMarketplaceChart
  );

  const isLoading = analyticsLoading || chartLoading;
  const [soloKey, setSoloKey] = useState(null);

  useEffect(() => {
    if (!userId) return;
    dispatch(actions.fetchMarketplaceAnalytics({ userId }));
    dispatch(actions.fetchMarketplaceChart({ userId }));
  }, [userId]);

  // ── Derive months & type keys from data ────
  const months = [...new Set((chartData || []).map((r) => r.month))];

  const seriesMap = {};
  (chartData || []).forEach(({ type, month, count }) => {
    // Merge call + whatsapp into contact
    const key = MERGED_INTO_CONTACT.includes(type) ? "contact" : type;
    if (!seriesMap[key]) seriesMap[key] = {};
    seriesMap[key][month] = (seriesMap[key][month] || 0) + Number(count);
  });

  const dynamicKeys = Object.keys(seriesMap);
  const knownOrder = Object.keys(ACTION_COLORS);
  const actionKeys = [
    ...knownOrder.filter((k) => dynamicKeys.includes(k)),
    ...dynamicKeys.filter((k) => !knownOrder.includes(k))
  ];

  let fallbackIndex = 0;
  const getColor = (key) =>
    ACTION_COLORS[key] ??
    FALLBACK_COLORS[fallbackIndex++ % FALLBACK_COLORS.length];

  const allSeries = actionKeys.map((key) => ({
    key,
    name: key.charAt(0).toUpperCase() + key.slice(1),
    color: getColor(key),
    data: months.map((m) => seriesMap[key][m] ?? null),
    connectNulls: false
  }));

  // ── Summary: always show MARKETPLACE_STATS_ORDER keys, value = 0 fallback ────
  const getMergedValue = (key) => {
    if (!analyticsData) return 0;
    if (key === "contact") {
      // Sum from seriesMap since analytics `contact` excludes call+whatsapp
      const contactFromChart = seriesMap["contact"]
        ? Object.values(seriesMap["contact"]).reduce((a, b) => a + b, 0)
        : 0;
      return contactFromChart;
    }
    return Number(analyticsData?.[key] ?? 0);
  };

  const extraSummaryKeys = actionKeys.filter(
    (k) =>
      !MARKETPLACE_STATS_ORDER.includes(k) && !MERGED_INTO_CONTACT.includes(k)
  );

  const sortedSummaryKeys = [...MARKETPLACE_STATS_ORDER, ...extraSummaryKeys];

  const statsSummary = sortedSummaryKeys.map((key) => ({
    label:
      key === "contact"
        ? "Contact (call + whatsapp)"
        : key.charAt(0).toUpperCase() + key.slice(1),
    value: getMergedValue(key)
  }));

  const chartSeries =
    allSeries.length > 0
      ? allSeries
      : [
          {
            name: "Empty",
            data: [0],
            color: "transparent",
            enableMouseTracking: false,
            showInLegend: false
          }
        ];

  useEffect(() => {
    if (!chartRef.current) return;
    if (isLoading) return;

    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
      chartInstanceRef.current = null;
    }

    chartInstanceRef.current = Highcharts.chart(chartRef.current, {
      chart: {
        type: "line",
        backgroundColor: "transparent",
        style: { fontFamily: "inherit" },
        marginTop: 30
      },
      title: {
        text: "User Marketplace Insights",
        style: { fontSize: "14px", fontWeight: "600", color: "#374151" }
      },
      xAxis: {
        categories: months.length ? months : [""],
        labels: { style: { color: "#9ca3af", fontSize: "12px" } },
        title: { text: "Month", style: { color: "#9ca3af", fontSize: "12px" } },
        lineColor: "#e5e7eb",
        tickColor: "transparent"
      },
      yAxis: {
        min: 0,
        allowDecimals: false,
        title: { text: "Count", style: { color: "#9ca3af", fontSize: "12px" } },
        labels: { style: { color: "#9ca3af", fontSize: "12px" } },
        gridLineColor: "#f3f4f6"
      },
      legend: { enabled: false },
      tooltip: {
        outside: true,
        useHTML: true,
        backgroundColor: "#fff",
        borderColor: "transparent",
        borderRadius: 8,
        borderWidth: 0,
        shadow: {
          color: "rgba(0,0,0,0.08)",
          offsetX: 0,
          offsetY: 4,
          opacity: 1,
          width: 12
        },
        padding: 12,
        style: { color: "#1f1f1f" },
        shape: "square",
        formatter: function () {
          const point = this.point;
          const seriesName = this.series.name;

          if (point.y === null || point.y === undefined) return "";

          return `
    <div style="display:flex;align-items:center;gap:8px;border-left:4px solid ${point.color};padding-left:8px;font-size:14px;font-weight:500;min-width:100px;">
      <div>
        <div style="font-size:12px;color:rgba(31,31,31,0.60);font-weight:500;line-height:16px;padding-bottom:8px;">${seriesName}</div>
        <div><span style="font-weight:500;color:#1F1F1F;font-size:20px;line-height:28px;">${point.y}</span></div>
      </div>
    </div>
  `;
        }
      },
      plotOptions: {
        line: {
          lineWidth: 2.5,
          marker: { enabled: true, radius: 4, symbol: "circle" },
          dataLabels: {
            enabled: true,
            style: {
              fontSize: "11px",
              fontWeight: "500",
              color: "#6b7280",
              textOutline: "none"
            },
            formatter: function () {
              return this.y !== null ? this.y : "";
            }
          }
        }
      },
      series: chartSeries.map((s) => ({
        name: s.name,
        color: s.color,
        data: s.data,
        connectNulls: s.connectNulls,
        visible: true
      })),
      credits: { enabled: false }
    });

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [chartData, isLoading]);

  const handleLegendClick = (key) => {
    if (!chartInstanceRef.current) return;

    setSoloKey((prev) => {
      const nextSolo = prev === key ? null : key;

      actionKeys.forEach((k) => {
        const name = k.charAt(0).toUpperCase() + k.slice(1);
        const hcSeries = chartInstanceRef.current.series.find(
          (s) => s.name === name
        );
        if (hcSeries) {
          const hasData =
            seriesMap[k] && Object.values(seriesMap[k]).some((v) => v > 0);
          const shouldShow = nextSolo === null || nextSolo === k || !hasData;
          hcSeries.setVisible(shouldShow, false);
        }
      });
      chartInstanceRef.current.redraw();
      return nextSolo;
    });
  };

  return (
    <Card>
      <SummaryRow>
        {statsSummary.map((item) => (
          <SummaryItem key={item.label}>
            <SummaryLabel>{item.label}</SummaryLabel>
            <SummaryValue>{item.value}</SummaryValue>
          </SummaryItem>
        ))}
      </SummaryRow>

      {isLoading ? (
        <LoadingSpinner
          color="#797979"
          text="Loading..."
          fontSize={14}
          size={16}
          className="loading-spinner"
        />
      ) : (
        <>
          <ChartContainer ref={chartRef} />
          <LegendRow>
            {allSeries.map(({ key, name, color }) => {
              const isActive = soloKey === null || soloKey === key;
              return (
                <LegendChip
                  key={key}
                  color={color}
                  active={isActive}
                  onClick={() => handleLegendClick(key)}
                  title={soloKey === key ? "Show all" : `Show only ${name}`}
                >
                  <LegendDot color={color} active={isActive} />
                  {name}
                </LegendChip>
              );
            })}
          </LegendRow>
        </>
      )}
    </Card>
  );
};

export default MarketplaceStats;
